import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, CalendarDays, Heart, Star, Loader2, MessageSquare, User, RefreshCw, Home, Images, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import CleanerCard from "@/components/CleanerCard";
import BookingStatusBadge from "@/components/BookingStatusBadge";
import PropertiesTab from "@/components/PropertiesTab";
import TurnoverReviewModal from "@/components/TurnoverReviewModal";
import NoShowCases, { ReportNoShowButton } from "@/components/NoShowPanel";
import { useAuth } from "@/context/AuthContext";
import ServiceFeeOffer from "@/components/ServiceFeeOffer";
import AccountSecurity from "@/components/AccountSecurity";
import QuotesPanel from "@/components/QuotesPanel";

const ACTIVE = ["awaiting_payment", "pending", "confirmed", "awaiting_approval"];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  // /host and ?tab=properties open Host Mode directly from the main menu.
  const initialTab = location.pathname === "/host" ? "properties" : (params.get("tab") || "overview");
  const [active, setActive] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [favs, setFavs] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");

  const load = async () => {
    const [s, b, f] = await Promise.all([
      api.get("/dashboard/stats"), api.get("/bookings/mine"), api.get("/favourites"),
    ]);
    setStats(s.data); setBookings(b.data.bookings); setFavs(f.data.favourites);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.post("/bookings/cancel", { booking_id: id }); toast.success("Booking cancelled & refund initiated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.booking_id, rating, text: reviewText });
      toast.success("Thanks for your review!");
      setReviewFor(null); setReviewText(""); setRating(5); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "find", label: "Find a Cleaner", icon: Search },
    { key: "upcoming", label: "Upcoming", icon: CalendarDays },
    { key: "history", label: "History", icon: RefreshCw },
    { key: "favourites", label: "Favourites", icon: Heart },
    { key: "quotes", label: "Quotes", icon: FileText },
    { key: "properties", label: "Airbnb Host Mode", icon: Home },
    { key: "account", label: "My Account", icon: User },
  ];

  // "Find a Cleaner" routes into the existing search journey rather than switching tabs.
  const onTab = (key) => (key === "find" ? navigate("/search") : setActive(key));

  const renderBooking = (b) => (
    <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-semibold">{b.service_name}</p>
          <BookingStatusBadge status={b.status} />
        </div>
        <Link to={`/cleaners/${b.cleaner_id}`} className="text-sm text-accent font-medium inline-flex items-center gap-1 mt-1 hover:underline" data-testid={`booking-cleaner-${b.booking_id}`}>
          <User className="h-3.5 w-3.5" /> {b.cleaner_name}
        </Link>
        <p className="text-sm text-muted-foreground mt-0.5">{b.date} at {b.start_time} · {b.duration_hours}h · {b.address?.city} {b.address?.postcode}</p>
        <p className="text-sm font-medium mt-1">£{b.price_total.toFixed(2)}</p>
        {b.status === "declined" && <p className="text-xs text-muted-foreground mt-1">Cleaner unavailable — your refund has been processed (3–5 working days).</p>}
        {b.status === "cancelled" && b.refund_type && <p className="text-xs text-muted-foreground mt-1">Cancelled — {b.refund_type === "half" ? "50%" : "100%"} refund processed.</p>}
        {b.property_summary?.name && <p className="text-xs text-accent font-medium mt-1">{b.property_summary.name}</p>}
        {b.status === "awaiting_approval" && <p className="text-xs text-muted-foreground mt-1">Your cleaner has finished and uploaded photos — review them to complete the job.</p>}
      </div>
      <div className="flex gap-2 shrink-0">
        {b.status === "awaiting_approval" && (
          <button onClick={() => setApproveFor(b)} className="px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors inline-flex items-center gap-1" data-testid={`review-turnover-${b.booking_id}`}>
            <Images className="h-4 w-4" /> Review photos
          </button>
        )}
        {(b.status === "confirmed" || b.status === "awaiting_approval") && (
          <button onClick={() => navigate("/messages", { state: { to: b.cleaner_id, name: b.cleaner_name } })} className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors inline-flex items-center gap-1" data-testid={`msg-cleaner-${b.booking_id}`}>
            <MessageSquare className="h-4 w-4" /> Message
          </button>
        )}
        {(b.status === "confirmed" || b.status === "awaiting_approval") && !b.noshow_case_id && (
          <ReportNoShowButton booking={b} onReported={load} />
        )}
        {ACTIVE.includes(b.status) && (
          <button onClick={() => cancel(b.booking_id)} className="px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`cancel-${b.booking_id}`}>Cancel</button>
        )}
        {b.status === "completed" && (
          <button onClick={() => setReviewFor(b)} className="px-4 py-2 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors" data-testid={`review-${b.booking_id}`}>Leave review</button>
        )}
        {["completed", "cancelled", "declined"].includes(b.status) && (
          <button onClick={() => navigate(`/book/${b.cleaner_id}?service=${b.service_slug}`)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors" data-testid={`rebook-${b.booking_id}`}>Rebook</button>
        )}
      </div>
    </div>
  );

  const upcoming = bookings.filter((b) => ACTIVE.includes(b.status));
  const history = bookings.filter((b) => !ACTIVE.includes(b.status));

  return (
    <DashboardShell title="Customer Dashboard" tabs={tabs} active={active} onTab={onTab}>
      {!stats ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : (
        <>
          {active === "overview" && (
            <div className="space-y-6">
              <div className="bg-card rounded-2xl border border-primary/5 p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="find-cleaner-promo">
                <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                  <Search className="h-6 w-6 text-accent" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold">Find a Cleaner</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Search trusted cleaners near you by postcode, service and date, then book in a
                    couple of taps.
                  </p>
                </div>
                <button onClick={() => navigate("/search")} className="sm:ml-auto shrink-0 px-5 py-2.5 rounded-full bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-1.5" data-testid="find-cleaner-btn">
                  <Search className="h-4 w-4" /> Find a Cleaner
                </button>
              </div>
              <ServiceFeeOffer />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="customer-stats">
                <Stat label="Total bookings" value={stats.total_bookings} />
                <Stat label="Upcoming" value={stats.upcoming} />
                <Stat label="Awaiting acceptance" value={stats.pending} />
                <Stat label="Saved cleaners" value={stats.favourites} />
              </div>

              <div className="bg-card rounded-2xl border border-primary/5 p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="host-mode-promo">
                <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                  <Home className="h-6 w-6 text-accent" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold">Airbnb Host Mode</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Renting out a place? Save each property with its turnover checklist and contactless
                    access details, then book a trusted cleaner in two taps.
                  </p>
                </div>
                <button onClick={() => setActive("properties")} className="sm:ml-auto shrink-0 px-5 py-2.5 rounded-full bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors" data-testid="open-host-mode">
                  Open Host Mode
                </button>
              </div>
            </div>
          )}

          {active === "account" && (
            <div className="space-y-6 max-w-xl">
            <div className="bg-card rounded-2xl border border-primary/5 p-6 space-y-4" data-testid="customer-account">
              <div>
                <h3 className="font-display font-semibold">Account details</h3>
                <p className="text-sm text-muted-foreground mt-1" data-testid="account-name">{user?.name}</p>
                <p className="text-sm text-muted-foreground" data-testid="account-email">{user?.email}</p>
                <p className="text-sm text-muted-foreground capitalize">Signed in as {user?.role}</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <button onClick={() => setActive("upcoming")} className="px-4 py-2.5 rounded-full border border-border font-medium hover:bg-secondary transition-colors" data-testid="account-bookings-btn">My bookings</button>
                <button onClick={() => setActive("history")} className="px-4 py-2.5 rounded-full border border-border font-medium hover:bg-secondary transition-colors" data-testid="account-history-btn">Booking history</button>
                <button onClick={() => navigate("/messages")} className="px-4 py-2.5 rounded-full border border-border font-medium hover:bg-secondary transition-colors inline-flex items-center justify-center gap-1.5" data-testid="account-messages-btn">
                  <MessageSquare className="h-4 w-4" /> Conversations
                </button>
              </div>
              <button onClick={logout} className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors" data-testid="customer-logout-btn">Log out</button>
            </div>
            <AccountSecurity />
            </div>
          )}

          {active === "upcoming" && (
            <div className="space-y-4" data-testid="customer-upcoming">
              <NoShowCases role="customer" />
              {upcoming.length === 0 ? <Empty text="No upcoming bookings." cta="Find a cleaner" onClick={() => navigate("/search")} /> :
                upcoming.map(renderBooking)}
            </div>
          )}

          {active === "history" && (
            <div className="space-y-4" data-testid="customer-history">
              {history.length === 0 ? <Empty text="No past bookings yet." cta="Find a cleaner" onClick={() => navigate("/search")} /> :
                history.map(renderBooking)}
            </div>
          )}

          {active === "quotes" && (
            <div className="max-w-3xl" data-testid="customer-quotes"><QuotesPanel role="customer" /></div>
          )}

          {active === "favourites" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="customer-favourites">
              {favs.length === 0 ? <Empty text="No favourites saved yet." cta="Browse cleaners" onClick={() => navigate("/search")} /> :
                favs.map((c) => <CleanerCard key={c.cleaner_id} c={c} />)}
            </div>
          )}
          {active === "properties" && <PropertiesTab />}
        </>
      )}

      {approveFor && (
        <TurnoverReviewModal booking={approveFor} onClose={() => setApproveFor(null)}
          onApproved={() => { setApproveFor(null); load(); }} />
      )}
      {reviewFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-5" onClick={() => setReviewFor(null)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <h3 className="font-display text-xl font-semibold">Review {reviewFor.cleaner_name}</h3>
            <div className="flex gap-1 my-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}><Star className={`h-8 w-8 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-border"}`} /></button>
              ))}
            </div>
            <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Tell others about your experience…" rows={4} className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="review-text" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setReviewFor(null)} className="flex-1 py-2.5 rounded-full border border-border font-medium">Cancel</button>
              <button onClick={submitReview} className="flex-1 py-2.5 rounded-full bg-accent text-white font-medium" data-testid="submit-review">Submit</button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function Stat({ label, value }) {
  return <div className="bg-card rounded-2xl border border-primary/5 p-6"><p className="font-display text-3xl font-semibold">{value}</p><p className="text-sm text-muted-foreground mt-1">{label}</p></div>;
}
function Empty({ text, cta, onClick }) {
  return <div className="col-span-full text-center py-16"><p className="text-muted-foreground">{text}</p><button onClick={onClick} className="mt-4 px-6 py-2.5 rounded-full bg-accent text-white font-medium">{cta}</button></div>;
}
